import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as messageService from '../../services/messageService';
import { API_BASE_URL } from '../../config/api';
import './MobileGroupChatPage.css';

const MAX_MEMBERS = 49;

const getAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (avatar.startsWith('http')) return avatar;
  if (avatar.startsWith('/uploads')) return avatar;
  return `${API_BASE_URL}${avatar}`;
};

const MobileGroupChatPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [activeTab, setActiveTab] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [recentUsers, setRecentUsers] = useState([]);
  const [contactUsers, setContactUsers] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [selected, setSelected] = useState([]); // array of user objects
  const [creating, setCreating] = useState(false);

  // ── Fetch recent chat users ──────────────────────────────────────────
  const fetchRecent = useCallback(async () => {
    if (!user?.id) return;
    try {
      setLoadingRecent(true);
      const convs = await messageService.getConversations(user.id);
      const users = (Array.isArray(convs) ? convs : [])
        .map(c => c.sender)
        .filter(Boolean)
        .filter(u => u.id !== user.id)
        // deduplicate by id
        .filter((u, i, arr) => arr.findIndex(x => x.id === u.id) === i);
      setRecentUsers(users);
    } catch (_) {
      setRecentUsers([]);
    } finally {
      setLoadingRecent(false);
    }
  }, [user?.id]);

  // ── Fetch contacts tab ───────────────────────────────────────────────
  const fetchContacts = useCallback(async () => {
    if (!user?.id || contactUsers.length > 0) return;
    try {
      setLoadingContacts(true);
      const data = await messageService.getContacts(user.id);
      const users = (Array.isArray(data) ? data : data?.users || [])
        .filter(u => u.id !== user.id)
        .filter((u, i, arr) => arr.findIndex(x => x.id === u.id) === i);
      setContactUsers(users);
    } catch (_) {
      setContactUsers([]);
    } finally {
      setLoadingContacts(false);
    }
  }, [user?.id, contactUsers.length]);

  useEffect(() => { fetchRecent(); }, [fetchRecent]);

  useEffect(() => {
    if (activeTab === 'contacts') fetchContacts();
  }, [activeTab, fetchContacts]);

  // ── Derived list based on tab + search ───────────────────────────────
  const sourceList = activeTab === 'recent' ? recentUsers : contactUsers;
  const filtered = searchQuery.trim()
    ? sourceList.filter(u =>
        (u.name || u.companyName || '').toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sourceList;

  const isLoading = activeTab === 'recent' ? loadingRecent : loadingContacts;

  // ── Selection toggle ─────────────────────────────────────────────────
  const toggleSelect = (u) => {
    setSelected(prev => {
      if (prev.find(x => x.id === u.id)) return prev.filter(x => x.id !== u.id);
      if (prev.length >= MAX_MEMBERS) return prev; // cap at 49
      return [...prev, u];
    });
  };

  const isSelected = (u) => !!selected.find(x => x.id === u.id);

  // ── Create group ─────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (selected.length === 0 || creating) return;
    setCreating(true);
    try {
      const result = await messageService.createGroupChat(
        selected.map(u => u.id),
        user.id
      );
      const groupId = result?.group?.id || result?.id || result?.conversationId;
      if (groupId) {
        navigate(`/mobile/chat/${groupId}`);
      } else {
        navigate('/mobile/messenger');
      }
    } catch (err) {
      alert(err.message || 'Failed to create group. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="mgc-page">
      {/* ── Header ── */}
      <div className="mgc-header">
        <button
          className="mgc-back-btn"
          onClick={() => navigate('/mobile/messenger')}
          aria-label="Close"
          type="button"
        >
          ✕
        </button>
        <h1 className="mgc-title">Add group members</h1>
      </div>

      {/* ── Search ── */}
      <div className="mgc-search-wrap">
        <svg className="mgc-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          className="mgc-search-input"
          type="text"
          placeholder="Search"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="mgc-search-clear" onClick={() => setSearchQuery('')} type="button">✕</button>
        )}
      </div>

      {/* ── Tabs ── */}
      <div className="mgc-tabs">
        <button
          className={`mgc-tab ${activeTab === 'recent' ? 'active' : ''}`}
          onClick={() => setActiveTab('recent')}
          type="button"
        >
          Recent chats
        </button>
        <button
          className={`mgc-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
          type="button"
        >
          Contacts
        </button>
      </div>

      {/* ── User List ── */}
      <div className="mgc-list">
        {isLoading ? (
          <div className="mgc-loading">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="mgc-skel-row">
                <div className="mgc-skel-avatar" />
                <div className="mgc-skel-body">
                  <div className="mgc-skel-name" />
                  <div className="mgc-skel-sub" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="mgc-empty">
            <div className="mgc-empty-illustration">
              <svg viewBox="0 0 160 140" fill="none" xmlns="http://www.w3.org/2000/svg">
                <ellipse cx="80" cy="110" rx="60" ry="18" fill="#e8f0fe" />
                <rect x="40" y="30" width="80" height="70" rx="10" fill="#dce8ff" />
                <rect x="50" y="20" width="60" height="70" rx="8" fill="#edf3ff" />
                <rect x="58" y="35" width="30" height="5" rx="2.5" fill="#b0c4f8" />
                <rect x="58" y="46" width="44" height="5" rx="2.5" fill="#b0c4f8" />
                <rect x="58" y="57" width="22" height="5" rx="2.5" fill="#b0c4f8" />
                <circle cx="105" cy="52" r="20" fill="#fff3e8" />
                <circle cx="105" cy="45" r="6" fill="#ffb87a" />
                <path d="M92 68c0-7 6-13 13-13s13 6 13 13" fill="#ffb87a" />
                <rect x="108" y="55" width="12" height="18" rx="4" fill="#ff8c00" />
                <rect x="108" y="68" width="12" height="10" rx="2" fill="#ff8c00" />
                <rect x="104" y="64" width="4" height="12" rx="2" fill="#ffb87a" />
                <rect x="120" y="64" width="4" height="9" rx="2" fill="#ffb87a" />
                <rect x="110" y="73" width="4" height="6" rx="2" fill="#ff8c00" />
                <rect x="116" y="73" width="4" height="6" rx="2" fill="#ff8c00" />
                <rect x="82" y="38" width="20" height="14" rx="3" fill="#d0e2ff" />
                <circle cx="85" cy="43" r="1.5" fill="#7aadff" />
                <circle cx="89" cy="43" r="1.5" fill="#7aadff" />
                <circle cx="93" cy="43" r="1.5" fill="#7aadff" />
              </svg>
            </div>
            <p className="mgc-empty-text">No search results available.</p>
          </div>
        ) : (
          filtered.map(u => {
            const name = u.name || u.companyName || 'Unknown';
            const avatarUrl = getAvatarUrl(u.avatar || u.logo);
            const initial = name.charAt(0).toUpperCase();
            const sel = isSelected(u);
            return (
              <button
                key={u.id}
                className={`mgc-user-row ${sel ? 'mgc-user-selected' : ''}`}
                onClick={() => toggleSelect(u)}
                type="button"
              >
                <div className="mgc-user-avatar">
                  {avatarUrl
                    ? <img src={avatarUrl} alt={name} />
                    : <span>{initial}</span>
                  }
                </div>
                <div className="mgc-user-info">
                  <span className="mgc-user-name">{name}</span>
                  {u.country && <span className="mgc-user-sub">{u.country}</span>}
                </div>
                <div className={`mgc-checkbox ${sel ? 'mgc-checkbox--checked' : ''}`}>
                  {sel && (
                    <svg viewBox="0 0 12 10" fill="none">
                      <polyline points="1 5 4.5 8.5 11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* ── Bottom bar ── */}
      <div className="mgc-bottom-bar">
        <span className="mgc-selected-label">
          {selected.length > 0
            ? selected.map(u => u.name || u.companyName || 'User').join(', ')
            : 'Selected:'}
        </span>
        <button
          className={`mgc-confirm-btn ${selected.length > 0 ? 'mgc-confirm-btn--active' : ''}`}
          onClick={handleConfirm}
          disabled={selected.length === 0 || creating}
          type="button"
        >
          {creating ? 'Creating…' : `Confirm (${selected.length}/${MAX_MEMBERS})`}
        </button>
      </div>
    </div>
  );
};

export default MobileGroupChatPage;
